import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import type { GraphQLResult } from 'aws-amplify/api'
import { sortBy } from 'lodash'
import type { Customer } from '@skop-app/lib-domain/types'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  customersByOrganizationQuery,
  getCustomerByIdQuery,
} from '@/services/graphql'

const client = getAmplifyClient()

export const useCustomersByOrganization = (id: string | undefined) => {
  return useQuery({
    queryKey: ['customersByOrganization', id],
    queryFn: async () => await getCustomersByOrganization(id),
    enabled: !!id,
    select: (data) => sortBy(data?.items, 'lastName'),
  })
}

const getCustomersByOrganization = async (id: string | undefined) => {
  try {
    const result = (await client.graphql({
      query: customersByOrganizationQuery,
      variables: {
        organizationID: id,
        filter: { deleted: { attributeExists: false } },
      },
    })) as GraphQLResult<{ customersByOrganization: { items: Customer[] } }>

    return result.data.customersByOrganization
  } catch (error) {
    console.error('Error fetching customers by organization:', error)
    return {
      items: [],
    }
  }
}

export const useCustomer = (id: string | undefined) => {
  return useQuery({
    queryKey: ['customer', id],
    queryFn: async () => await getCustomerById(id),
    enabled: !!id,
    select: (data) => data,
  })
}

const getCustomerById = async (id: string | undefined) => {
  try {
    const result = (await client.graphql({
      query: getCustomerByIdQuery,
      variables: {
        id,
      },
    })) as GraphQLResult<{ getCustomer: Customer }>

    return result.data.getCustomer
  } catch (error) {
    console.error('Error fetching customer:', error)
    return null
  }
}

export const getCustomerInformationsWithSiret = async (siret: string) => {
  const removeSpacesInSiret = siret.replace(/\s/g, '')
  try {
    const reponse = await axios.get('/api/siret/get/' + removeSpacesInSiret)
    return reponse.data
  } catch (error) {
    console.error('error fetching customer informations with SIRET', error)
  }
}
